import { useState } from 'react'
import { useRotateKey } from '../../hooks/useRotateKey'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { ErrorBanner } from '../../components/ErrorBanner'
import { apiErrorMessage } from '../../api/client'

// RotateKeyDialog confirms the forensic per-glow HMAC rotation
// (CLAUDE.md §6). Split out of SettingsTab (FE-4). Rotating advances the
// glow's salt slot, invalidating every active session immediately —
// Private-glow users re-sign-in, Public-glow visitors get a fresh
// anonymous JWT on their next request.
export function RotateKeyDialog({
  glowID,
  onClose,
}: {
  glowID: string
  onClose: () => void
}) {
  const rotate = useRotateKey(glowID)
  const [error, setError] = useState<string | null>(null)
  const [doneKID, setDoneKID] = useState<string | null>(null)
  const onConfirm = async () => {
    setError(null)
    try {
      const res = await rotate.mutateAsync()
      setDoneKID(res.hmac_salt_kid)
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not rotate signing key.'))
    }
  }
  return (
    <Modal onClose={onClose} labelledBy="rotate-key-title">
      <h3 id="rotate-key-title" className="text-lg font-semibold">
        Rotate the signing key?
      </h3>
      {doneKID ? (
          <>
            <p className="mt-2 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
              Signing key rotated. All sessions are invalidated; the new
              salt slot is <code className="font-mono">{doneKID}</code>.
            </p>
            <div className="mt-4 flex justify-end">
              <Button onClick={onClose}>Close</Button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-cream/70">
              Every user with an active session on this glow will be signed
              out on their next request. Private-glow users will need to
              sign in again via magic link or social signin; Public-glow
              visitors will get a fresh anonymous session. Existing data is
              not affected.
            </p>
            <p className="mt-2 text-sm text-cream/70">
              This can&apos;t be undone — the previous key material is
              replaced. Continue?
            </p>
            {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={rotate.isPending}>
                Cancel
              </Button>
              <Button variant="danger" onClick={onConfirm} disabled={rotate.isPending}>
                {rotate.isPending ? 'Rotating…' : 'Rotate signing key'}
              </Button>
            </div>
          </>
        )}
    </Modal>
  )
}
